import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandableChips from "components/ExpandableChips";
import IdBadge from "components/IdBadge";
import { useState } from "react";
import CreateSystemDialog from "./CreateSystemDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import SystemDetailsDrawer from "./SystemDetailsDrawer";
import t from "./translations";

const COLUMN_COUNT = 4;

export default function SystemsTab() {
  const { systems, systemAxisLinksBySystem, loading } = useLidarClearance();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const selectedSystem =
    systems.find(
      (system) => regionKey(system.region, system.id) === selectedKey
    ) ?? null;

  return (
    <Box data-testid="systems-tab">
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          data-testid="systems-tab.new-system"
        >
          {t.NewSystemButton}
        </Button>
      </Box>
      <TableContainer component={Paper} data-testid="systems-table">
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: 90 }}>
                ID
              </TableCell>
              <TableCell>{t.NameColumn}</TableCell>
              <TableCell sx={{ width: 200 }}>{t.CustomerColumn}</TableCell>
              <TableCell sx={{ width: 260 }}>{t.AxesColumn}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {systems.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {systems.map((system) => {
              const key = regionKey(system.region, system.id);
              // Axis ids are only unique within a region, but every axis
              // linked to one system is necessarily in that system's own
              // region, so a plain id key is safe for this per-row lookup.
              const links = systemAxisLinksBySystem.get(key) ?? [];
              const axisIds = links.map((entry) => entry.axis.id);
              const axisLabelMap = new Map(
                links.map((entry) => [entry.axis.id, entry.axis.name_en])
              );

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => setSelectedKey(key)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`systems-tab.row-${system.region}-${system.id}`}
                >
                  <TableCell align="center">
                    <IdBadge
                      prefix={system.region.toUpperCase()}
                      id={system.id}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {system.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{system.customer_id ?? t.GlobalLabel}</TableCell>
                  <TableCell>
                    <ExpandableChips
                      ids={axisIds}
                      labelMap={axisLabelMap}
                      max={3}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <SystemDetailsDrawer
        system={selectedSystem}
        onClose={() => setSelectedKey(null)}
      />
      <CreateSystemDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
